import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';

import {
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftEllipsisIcon,
  ClockIcon,
  DocumentIcon,
  PaperClipIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { Link } from '@oysterjs/ui/link';
import { Spinner } from '@oysterjs/ui/spinner';

import {
  CaraWorkspaceOverviewFieldsFragment,
  ClientArtifactFieldsFragment,
  ClientFileFieldsFragment,
  ClientNoteFieldsFragment,
  useGetClientArtifactsQuery,
  useGetClientFilesQuery,
  useGetClientNotesQuery,
  useSearchCaraWorkspacesQuery
} from '~/api/graphql';
import { artifactHref } from '~/pages/clients/client-documents';
import { ClientCard, ClientEmptyMessage } from './client-panels';
import {
  CLIENT_ACTIVITY_PAGE_SIZE,
  clientActivityArtifactsVariables,
  clientActivityFilesVariables,
  clientActivityWorkspacesVariables
} from '~/api/data/clients/variables';
import { dateFormatter } from '~/components/table-formatters';

type ActivityKind = 'workspace' | 'document' | 'file' | 'note';

/**
 * One thing that happened for a client.
 *
 * Assembled in the browser from the lists the client already has rather than
 * served as a feed. That covers what is associated with a client today:
 * workspaces, documents, uploads and notes. Calls, emails and service requests
 * have no client association yet, so they are absent rather than empty.
 */
export interface ActivityEntry {
  id: string;
  kind: ActivityKind;
  title: string;
  /** Joined with a middle dot; empty parts are dropped. */
  detail: string[];
  at: Date;
  link?: { label: string; href: string; external?: boolean };
}

/** A note's first line stands in for it; the notepad is where you read the rest. */
const noteSummary = (body: string): string => {
  const firstLine = body.trim().split('\n')[0];
  return firstLine.length > 120 ? `${firstLine.slice(0, 119)}…` : firstLine;
};

export const workspaceEntry = (workspace: CaraWorkspaceOverviewFieldsFragment): ActivityEntry => ({
  id: `workspace:${workspace.id}`,
  kind: 'workspace',
  title: 'Workspace created',
  detail: [workspace.title || 'Untitled workspace', workspace.agencyUser?.name ?? ''],
  at: new Date(workspace.createdAt),
  link: { label: 'Open workspace', href: `/app/workspaces/${workspace.id}` }
});

export const artifactEntry = (
  clientId: string,
  artifact: ClientArtifactFieldsFragment
): ActivityEntry => ({
  id: `artifact:${artifact.id}`,
  kind: 'document',
  title: 'Document generated',
  detail: [artifact.title || 'Untitled document', artifact.workspace.title],
  at: new Date(artifact.createdAt),
  link: { label: 'Open document', href: artifactHref(clientId, artifact) }
});

export const fileEntry = (file: ClientFileFieldsFragment): ActivityEntry => ({
  id: `file:${file.id}`,
  kind: 'file',
  title: 'File uploaded',
  detail: [file.name],
  at: new Date(file.createdAt),
  link: file.url ? { label: 'Open file', href: file.url, external: true } : undefined
});

export const noteEntry = (note: ClientNoteFieldsFragment): ActivityEntry => ({
  id: `note:${note.id}`,
  kind: 'note',
  title: `Note added by ${note.author?.name || note.author?.email || 'someone'}`,
  detail: [`“${noteSummary(note.body)}”`],
  at: new Date(note.createdAt)
});

/** One source of entries, newest first, with whether older ones remain unfetched. */
export interface ActivitySource {
  entries: ActivityEntry[];
  hasMore: boolean;
}

/**
 * The entries that can be shown in order, newest first.
 *
 * Each paged source is only known down to its oldest fetched entry. Below the
 * newest of those horizons, a source that has more could still hold an entry
 * that belongs above what is already listed, so everything past it is held
 * back until that source is paged further. `limiting` names the sources to page
 * next to move the horizon down.
 */
export const mergeActivity = (
  sources: ActivitySource[]
): { entries: ActivityEntry[]; limiting: number[] } => {
  let horizon = -Infinity;
  let limiting: number[] = [];
  sources.forEach((source, index) => {
    if (!source.hasMore) return;
    const oldest = source.entries[source.entries.length - 1]?.at.getTime() ?? Infinity;
    if (oldest > horizon) {
      horizon = oldest;
      limiting = [index];
    } else if (oldest === horizon) {
      limiting.push(index);
    }
  });

  // Ties break on id so the order is stable across renders -- a backfilled
  // client can have many rows sharing a timestamp.
  const entries = sources
    .flatMap((source) => source.entries)
    .filter((entry) => entry.at.getTime() >= horizon)
    .sort((a, b) => b.at.getTime() - a.at.getTime() || a.id.localeCompare(b.id));
  return { entries, limiting };
};

const timeFormatter = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
const weekdayFormatter = new Intl.DateTimeFormat('en-US', { weekday: 'short' });

/**
 * "Today 9:42 AM" / "Yesterday 2:08 PM" / "Mon 11:03 AM" / "Aug 8, 2026". The
 * past week is placed by time of day, because that is how someone remembers it;
 * older entries by date. The locale is pinned so this does not vary by machine.
 */
export const formatActivityTime = (at: Date, now: Date = new Date()): string => {
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  const startOfDay = (daysAgo: number) => {
    const day = new Date(midnight);
    day.setDate(day.getDate() - daysAgo);
    return day.getTime();
  };
  const time = at.getTime();
  if (time > now.getTime()) return dateFormatter.format(at);
  if (time >= startOfDay(0)) return `Today ${timeFormatter.format(at)}`;
  if (time >= startOfDay(1)) return `Yesterday ${timeFormatter.format(at)}`;
  if (time >= startOfDay(6)) return `${weekdayFormatter.format(at)} ${timeFormatter.format(at)}`;
  return dateFormatter.format(at);
};

const KIND_STYLES: Record<
  ActivityKind,
  { icon: React.ComponentType<{ className?: string }>; badge: string; glyph: string }
> = {
  workspace: {
    icon: SparklesIcon,
    badge: 'bg-sky-50 dark:bg-sky-500/10',
    glyph: 'text-sky-600 dark:text-sky-400'
  },
  document: {
    icon: DocumentIcon,
    badge: 'bg-sky-50 dark:bg-sky-500/10',
    glyph: 'text-sky-600 dark:text-sky-400'
  },
  file: {
    icon: PaperClipIcon,
    badge: 'bg-sky-50 dark:bg-sky-500/10',
    glyph: 'text-sky-600 dark:text-sky-400'
  },
  note: {
    icon: ChatBubbleLeftEllipsisIcon,
    badge: 'bg-neutral-100 dark:bg-neutral-700',
    glyph: 'text-neutral-500 dark:text-neutral-300'
  }
};

const ActivityRow = ({ entry, connected }: { entry: ActivityEntry; connected: boolean }) => {
  const style = KIND_STYLES[entry.kind];
  const detail = entry.detail.filter(Boolean).join(' · ');
  return (
    <li className="relative flex gap-4 pb-6 last:pb-0">
      {connected && (
        <span
          aria-hidden="true"
          className="absolute bottom-0 left-4 top-9 w-px -translate-x-1/2 bg-neutral-200 dark:bg-neutral-700"
        />
      )}
      <span
        aria-hidden="true"
        className={clsx(
          'relative flex size-8 shrink-0 items-center justify-center rounded-full',
          style.badge
        )}
      >
        <style.icon className={clsx('size-4', style.glyph)} />
      </span>
      <div className="min-w-0 flex-1 pt-1">
        <div className="flex items-baseline justify-between gap-4">
          <p className="m-0 text-sm font-medium text-neutral-900 dark:text-white">{entry.title}</p>
          <time
            dateTime={entry.at.toISOString()}
            className="shrink-0 whitespace-nowrap text-sm text-neutral-400 dark:text-neutral-500"
          >
            {formatActivityTime(entry.at)}
          </time>
        </div>
        {detail && (
          <p className="m-0 mt-0.5 truncate text-sm text-neutral-500 dark:text-neutral-400">
            {detail}
          </p>
        )}
        {entry.link && (
          <Link
            href={entry.link.href}
            target={entry.link.external ? '_blank' : undefined}
            className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300"
          >
            {entry.link.label}
            <ArrowTopRightOnSquareIcon className="size-3.5" aria-hidden="true" />
          </Link>
        )}
      </div>
    </li>
  );
};

/** A paged activity source, from its cache-first query. */
const usePagedSource = <T,>(
  result: {
    loading: boolean;
    fetchMore: (options: { variables: { after: string } }) => Promise<unknown>;
  },
  connection:
    | { edges: { node: T }[]; pageInfo: { hasNextPage: boolean; endCursor?: string | null } }
    | undefined,
  toEntry: (node: T) => ActivityEntry
) => {
  const entries = React.useMemo(
    () => (connection?.edges ?? []).map((edge) => toEntry(edge.node)),
    [connection?.edges, toEntry]
  );
  const endCursor = connection?.pageInfo.endCursor;
  const hasMore = !!connection?.pageInfo.hasNextPage && !!endCursor;
  const { fetchMore } = result;
  const loadMore = React.useCallback(() => {
    if (endCursor) void fetchMore({ variables: { after: endCursor } });
  }, [endCursor, fetchMore]);
  return { entries, hasMore, loading: result.loading, loadMore };
};

export const ClientActivity = ({ clientId }: { clientId: string }) => {
  // The overview's loader fetched the first page of each of these with the same
  // variables, so they answer from the cache; further pages go to the server.
  const workspacesResult = useSearchCaraWorkspacesQuery({
    variables: clientActivityWorkspacesVariables(clientId),
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  });
  const artifactsResult = useGetClientArtifactsQuery({
    variables: clientActivityArtifactsVariables(clientId),
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  });
  const filesResult = useGetClientFilesQuery({
    variables: clientActivityFilesVariables(clientId),
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  });
  const notesResult = useGetClientNotesQuery({
    variables: { clientId },
    fetchPolicy: 'cache-first'
  });

  const workspaces = usePagedSource(
    workspacesResult,
    workspacesResult.data?.searchCaraWorkspaces,
    workspaceEntry
  );
  const toArtifactEntry = React.useCallback(
    (artifact: ClientArtifactFieldsFragment) => artifactEntry(clientId, artifact),
    [clientId]
  );
  const artifacts = usePagedSource(
    artifactsResult,
    artifactsResult.data?.client?.artifacts,
    toArtifactEntry
  );
  const files = usePagedSource(filesResult, filesResult.data?.client?.files, fileEntry);
  const notes = React.useMemo(
    () => (notesResult.data?.clientNotes ?? []).map(noteEntry),
    [notesResult.data?.clientNotes]
  );

  const paged = [workspaces, artifacts, files];
  const { entries, limiting } = mergeActivity([...paged, { entries: notes, hasMore: false }]);

  const [limit, setLimit] = React.useState(CLIENT_ACTIVITY_PAGE_SIZE);
  const fetching = paged.some((source) => source.loading);
  const exhausted = limiting.length === 0;

  // Pages whichever source is holding the list back until there is enough to
  // fill the limit. Each fetch moves that source's horizon down, so this ends.
  React.useEffect(() => {
    if (entries.length >= limit || exhausted || fetching) return;
    limiting.forEach((index) => paged[index]?.loadMore());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries.length, limit, exhausted, fetching, limiting.join(',')]);

  const shown = entries.slice(0, limit);
  const canShowMore = entries.length > limit || !exhausted;

  return (
    <ClientCard title="Activity" subtitle="Work done for this client, newest first">
      {shown.length === 0 ? (
        fetching ? (
          <div className="flex justify-center py-6">
            <Spinner size="size-6" color="neutral" />
          </div>
        ) : (
          <ClientEmptyMessage
            className="py-6"
            icon={ClockIcon}
            title="No activity yet"
            description="Workspaces, documents, uploads and notes for this client will appear here."
          />
        )
      ) : (
        <>
          <ol className="m-0 list-none p-0">
            {shown.map((entry, index) => (
              <ActivityRow
                key={entry.id}
                entry={entry}
                connected={index < shown.length - 1 || canShowMore}
              />
            ))}
          </ol>
          {canShowMore && (
            <div className="mt-4 flex justify-center">
              <Headless.Button
                type="button"
                disabled={fetching && entries.length <= limit}
                onClick={() => setLimit((prev) => prev + CLIENT_ACTIVITY_PAGE_SIZE)}
                className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 disabled:text-neutral-400 dark:text-primary-400 dark:hover:bg-primary-500/10 dark:disabled:text-neutral-500"
              >
                {fetching && entries.length <= limit ? (
                  <>
                    <Spinner size="size-4" color="neutral" />
                    Loading…
                  </>
                ) : (
                  'Show more'
                )}
              </Headless.Button>
            </div>
          )}
        </>
      )}
    </ClientCard>
  );
};
